// DTOs del paro visto por el SUPERVISOR — detalle en S2 + sus dos acciones.
//
//   - GET   /api/supervisor/linea/:lineaId          (trae `paros_detalle`)
//   - PATCH /api/supervisor/paros/:id/corregir
//   - PATCH /api/supervisor/paros/:id/confirmar
//
// Ciclo completo del paro (decisión del responsable, 2026-08-14):
//   líder abre y cierra → si lo deja abierto lo cierra el TÉCNICO → el técnico
//   da el OK o propone corrección → el SUPERVISOR ve el detalle y lo CAMBIA o
//   lo CONFIRMA.
//
// Por qué un schema aparte y no engordar `paroSchema`: ese es el shape canónico
// que consume la captura del líder. Acá se sigue el patrón ya usado en
// `coordinador_linea_detalle.ts` — el registro viaja canónico y el
// enriquecimiento va como diccionario hermano indexado por paro_id.
//
// Diferencia con la corrección del TÉCNICO (`correccionParoProponerSchema`):
// la del técnico nace `estado='propuesta'` y espera aprobación; la del
// supervisor se aplica YA (él es quien aprueba), y queda en `validaciones_paro`
// como tipo='correccion' estado='aprobada' auto-resuelta.
import { z } from 'zod';
import { correccionVivaResumenSchema, estadoParoMttoSchema, } from './mantenimiento_paros_validacion.js';
const causalRefSchema = z.object({ id: z.string().uuid(), nombre: z.string() });
const personaRefSchema = z.object({
    id: z.string().uuid(),
    nombre: z.string(),
    apellido: z.string(),
});
// =============================================================================
// Detalle enriquecido del paro para S2
// =============================================================================
/**
 * Todo lo que el supervisor necesita ver de un paro sin salir de S2. Los ids y
 * las horas ya vienen en `registro.paros` (shape canónico); acá viajan los
 * NOMBRES y los sellos del ciclo, que el shape canónico no lleva.
 *
 * `estado_mtto` es el estado derivado del lado de mantenimiento (mig 072) y
 * `confirmado_sup_at` es el sello del supervisor (mig 079). Son ORTOGONALES a
 * propósito: un paro puede estar validado por el técnico y sin confirmar, o
 * corregido por el supervisor sin haber pasado nunca por mantenimiento.
 */
export const paroSupervisorDetalleSchema = z.object({
    id: z.string().uuid(),
    causal_madre: causalRefSchema,
    causal: causalRefSchema,
    categoria: z.object({
        id: z.string().uuid(),
        nombre: z.string(),
        es_mantenimiento: z.boolean(),
        /** FALSE = no cuenta como pérdida de disponibilidad (ej. la media). */
        afecta_disponibilidad: z.boolean(),
    }),
    estado_mtto: estadoParoMttoSchema,
    /** Sello del técnico de mantenimiento. */
    validado_mtto_at: z.string().datetime({ offset: true }).nullable(),
    validado_mtto_por: personaRefSchema.nullable(),
    /**
     * Quién cerró el paro cuando NO fue el líder por la vía normal (hoy: el
     * técnico de mantenimiento con hora backdateada). NULL = lo cerró el líder.
     */
    cerrado_por: personaRefSchema.nullable(),
    /** Sello del supervisor (mig 079). Se cae solo si el paro cambia después. */
    confirmado_sup_at: z.string().datetime({ offset: true }).nullable(),
    confirmado_sup_por: personaRefSchema.nullable(),
    /** Corrección del técnico esperando resolución, si hay. */
    correccion_viva: correccionVivaResumenSchema.nullable(),
    orden_trabajo_id: z.string().uuid().nullable(),
    /**
     * Flags computados por el backend (patrón `puede_invalidar` de S2): el server
     * ya resolvió scope de planta y estado, la UI solo habilita/deshabilita.
     * Ambos exigen paro CERRADO y registro padre en estado='valido' — un paro
     * abierto no tiene nada que confirmar todavía.
     */
    puede_confirmar: z.boolean(),
    puede_corregir: z.boolean(),
});
// =============================================================================
// PATCH /api/supervisor/paros/:id/corregir
// =============================================================================
/**
 * El supervisor cambia causal y/u horas del paro. Mismo bar de razón (≥10) que
 * la corrección del técnico: es el mismo acto sobre el mismo dato, cambia solo
 * quién lo firma.
 */
export const paroSupervisorCorregirSchema = z
    .object({
    /** La causal madre y la específica viajan JUNTAS (jerarquía, trigger en BD). */
    causal_madre_id: z.string().uuid().optional(),
    causal_id: z.string().uuid().optional(),
    hora_inicio: z.string().datetime({ offset: true }).optional(),
    hora_fin: z.string().datetime({ offset: true }).optional(),
    razon: z
        .string()
        .trim()
        .min(10, 'Describa la razón del cambio (mínimo 10 caracteres)')
        .max(500, 'Máximo 500 caracteres'),
})
    .refine((p) => p.causal_madre_id !== undefined ||
    p.causal_id !== undefined ||
    p.hora_inicio !== undefined ||
    p.hora_fin !== undefined, { message: 'Debe cambiar al menos un campo (causal u horas)' })
    .refine((p) => (p.causal_madre_id === undefined) === (p.causal_id === undefined), {
    message: 'La causal madre y la específica deben cambiarse juntas',
    path: ['causal_id'],
});
export const paroSupervisorCorregidoResponseSchema = z.object({
    registro_paro_id: z.string().uuid(),
    /** Fila de historial creada en validaciones_paro (tipo='correccion'). */
    validacion_id: z.string().uuid(),
    hora_inicio: z.string().datetime({ offset: true }),
    hora_fin: z.string().datetime({ offset: true }).nullable(),
    minutos_perdidos: z.number().nullable(),
    causal_madre_id: z.string().uuid(),
    causal_id: z.string().uuid(),
    /**
     * True si esta corrección dejó sin efecto una propuesta viva del técnico
     * (se marca 'rechazada' con razón de superposición en la misma tx). La UI
     * debe avisarlo: alguien del otro lado estaba esperando respuesta.
     */
    correccion_tecnico_superada: z.boolean(),
});
// =============================================================================
// PATCH /api/supervisor/paros/:id/confirmar
// =============================================================================
/**
 * Confirmar es un sello, no un cambio: nota opcional. La razón que exige
 * `validaciones_paro.razon` (≥10) la genera el server — pedirle 10 caracteres
 * al supervisor para decir "está bien" es fricción sin valor.
 */
export const paroSupervisorConfirmarSchema = z.object({
    nota: z.string().trim().min(5).max(500).nullable().default(null),
});
export const paroSupervisorConfirmadoResponseSchema = z.object({
    registro_paro_id: z.string().uuid(),
    validacion_id: z.string().uuid(),
    confirmado_sup_at: z.string().datetime({ offset: true }),
});
//# sourceMappingURL=supervisor_paros.js.map